import { create, useStore } from "zustand";
import {
  devtools,
  persist,
  createJSONStorage,
  type StateStorage,
} from "zustand/middleware";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import { get, set, del } from "idb-keyval"; // can use anything: IndexedDB, Ionic Storage, etc.
import { PatientSelectV2 } from "@api/src/types/patient";
import { FilterParams } from "@app/types/patient";
import type { PathwayConditions, PathwayPractitioner } from "@app/types/dashboard-pathway";
import { dashboardPathwayStore } from "@app/zustand/dashboard-pathway";
import { selectionKey } from "@app/components/dashboard-pathway/conditions";

// Custom storage object
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    // console.log(name, "has been retrieved");
    return (await get(name)) || null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    // console.log(name, "with value", value, "has been saved");
    await set(name, value);
  },
  removeItem: async (name: string): Promise<void> => {
    // console.log(name, "has been deleted");
    await del(name);
  },
};

export interface NLFilterCondition {
  column: string;
  op: "eq" | "neq" | "lt" | "lte" | "gt" | "gte" | "ilike" | "array_contains" | "range";
  value?: unknown;
  values?: string[];
  /** A `range`'s bounds, both inclusive, either open: a number, or an ISO day (PT-146). */
  min?: string | number;
  max?: string | number;
}

/** Terms of a search that no value of the board matched, under the axis they were asked on. */
export interface NLUnmatched {
  column: string;
  label: string;
  terms: string[];
}

/**
 * What a search kept, changed, added and removed of the board's conditions, by axis key (`periode` for
 * the period). Computed by the server from the board's conditions and the answer, not by the model.
 */
export interface NLChanges {
  kept: string[];
  changed: string[];
  added: string[];
  removed: string[];
}

/** The board's conditions and period when a question was asked: what its answer was written against. */
export interface NLSearchBase {
  conditions: PathwayConditions;
  periode: string | null;
  periodeStart: string | null;
  periodeEnd: string | null;
}

/** What `/nl-search/query` resolved: the WHOLE set of conditions the exploration board takes as its own. */
export interface NLSearchResult {
  parsed_filters: NLFilterCondition[];
  explanation: string | null;
  /** What the board could not apply, named by the server. Absent from the canned demo result. */
  unmatched?: NLUnmatched[];
  /** Absent when the board applied nothing when the question was asked, and from the demo result. */
  changes?: NLChanges | null;
  /**
   * The board as it was sent with the question. An edit made while the model answered is kept where the
   * answer did not touch the axis — the answer replaces what it was written against, not what came after.
   */
  base?: NLSearchBase;
}

export interface State {
  _hasHydrated: boolean;
  drawerOpen: boolean;
  /** Labels of the patients opened in this tab. Names: held in memory, never written to the browser. */
  patientsSelect: PatientSelectV2[];
  /** The dashboard's period. Its conditions live in `zustand/dashboard-pathway`. */
  filterParams: FilterParams;
  /**
   * The practitioner the dashboard and the patient picker are narrowed to — admin and fondateur
   * only, read through `usePractitionerFilter` which applies that gate (PT-111). The label is kept
   * so the chip can name it before the list loads.
   */
  practitionerFilter: PathwayPractitioner | null;
  loadingProgress: { current: number; total: number };
  loading: boolean;
  /** The question typed in the search bar, so that clearing the board's selection clears it too. */
  nlQuery: string;
  nlSearchResult: NLSearchResult | null;
  /**
   * The board's selection right after it applied that result (`selectionKey`), reported by the board,
   * which alone knows what it applied; `null` until it has. Not a component's state: the board may be
   * left for a patient page, the answer land meanwhile, and the board come back to apply it.
   */
  nlAppliedKey: string | null;
  lastLoadedPreviewKey: string;
  /** Last User.id observed by the layout. Drives the cross-user persisted-state
   * reset (HomeLayout). Null until first authenticated render after a logout
   * or fresh storage. */
  lastUserId: string | null;
}

interface Actions {
  setHasHydrated: (state: boolean) => void;
  setDrawerOpen: (state: boolean) => void;
  setPatientsSelect: (patientsSelect: PatientSelectV2[]) => void;
  setFilterParams: (filterParams: FilterParams) => void;
  setPractitionerFilter: (practitioner: PathwayPractitioner | null) => void;
  setLoadingProgress: (loadingProgress: {
    current: number;
    total: number;
  }) => void;
  setLoading: (loading: boolean) => void;
  setNlQuery: (query: string) => void;
  setNlSearchResult: (result: NLSearchResult | null) => void;
  setNlAppliedKey: (key: string | null) => void;
  setLastLoadedPreviewKey: (key: string) => void;
  setLastUserId: (id: string | null) => void;
}

export const EMPTY_FILTER_PARAMS: FilterParams = {
  periode: null,
  periodeStart: null,
  periodeEnd: null,
};

function initDrawerOpen() {
  const location = window.location;
  if (location.pathname.startsWith("/welcome")) return false;
  if (location.pathname.startsWith("/users")) return false;
  if (location.pathname.startsWith("/profile")) return false;
  if (location.pathname.startsWith("/stats")) return false;
  if (location.pathname.startsWith("/post")) return false;
  return window.innerWidth > 768 ? false : true;
}

const useMageXStore = create<State & Actions>()(
  devtools(
    persist(
      (set): State & Actions => ({
        drawerOpen: initDrawerOpen(),
        patientsSelect: [],
        filterParams: EMPTY_FILTER_PARAMS,
        practitionerFilter: null,
        loadingProgress: { current: 0, total: 0 },
        loading: true,
        nlQuery: "",
        nlSearchResult: null,
        nlAppliedKey: null,
        lastLoadedPreviewKey: "",
        lastUserId: null,
        setNlQuery: (query) => {
          set({ nlQuery: query });
        },
        // A new answer, or none: the board has applied none of it yet.
        setNlSearchResult: (result) => {
          set({ nlSearchResult: result, nlAppliedKey: null });
        },
        setNlAppliedKey: (key) => {
          set({ nlAppliedKey: key });
        },
        setLastLoadedPreviewKey: (key) => {
          set({ lastLoadedPreviewKey: key });
        },
        setLastUserId: (id) => {
          set({ lastUserId: id });
        },
        setLoadingProgress: (state) => {
          set({
            loadingProgress: state,
          });
        },
        setLoading: (state) => {
          set({
            loading: state,
          });
        },
        setPatientsSelect: (state) => {
          set({
            patientsSelect: state,
          });
        },
        setFilterParams: (state) => {
          set({
            filterParams: state,
          });
        },
        setPractitionerFilter: (practitioner) => {
          set({ practitionerFilter: practitioner });
        },
        setDrawerOpen: (state) => {
          set({
            drawerOpen: state,
          });
        },
        _hasHydrated: false,
        setHasHydrated: (state) => {
          set({
            _hasHydrated: state,
          });
        },
      }),
      {
        name: "mage-x-zustand-store",
        // v7: the flat dashboard's fields are gone; only what survives is carried over.
        // v8: the opened patients' names are no longer stored, and those already stored are dropped.
        version: 8,
        migrate: (persisted) => {
          const old = (persisted ?? {}) as {
            filterParams?: Partial<FilterParams>;
            practitionerFilter?: PathwayPractitioner | null;
            lastUserId?: string | null;
          };
          return {
            filterParams: {
              periode: old.filterParams?.periode ?? null,
              periodeStart: old.filterParams?.periodeStart ?? null,
              periodeEnd: old.filterParams?.periodeEnd ?? null,
            },
            practitionerFilter: old.practitionerFilter ?? null,
            lastUserId: old.lastUserId ?? null,
          } as State & Actions;
        },
        storage: createJSONStorage(() => storage),
        onRehydrateStorage: (state) => {
          return () => state.setHasHydrated(true);
        },
        partialize: (state) =>
          Object.fromEntries(
            Object.entries(state).filter(
              ([key]) =>
                ![
                  "drawerOpen",
                  "patientsSelect",
                  "loadingProgress",
                  "loading",
                  "nlQuery",
                  "nlSearchResult",
                  "nlAppliedKey",
                  "lastLoadedPreviewKey",
                ].includes(key),
            ),
          ),
      },
    ),
  ),
);

/**
 * Starts the dashboard over: the question, its answer, the period and the board's conditions. The
 * search's answer IS the board's whole set of filters (PT-146), so clearing one without the other left
 * a board narrowed by a search no longer on screen. The practitioner stays: it is the population the
 * board counts, not a filter of it.
 */
export function clearSearch(): void {
  dashboardPathwayStore.getState().clearConditions();
  useMageXStore.setState({
    nlQuery: "",
    nlSearchResult: null,
    nlAppliedKey: null,
    filterParams: EMPTY_FILTER_PARAMS,
  });
}

/**
 * Whether the dashboard's selection was changed by hand since the search set it: its explanation then no
 * longer describes the board. Read off the two stores, so it holds wherever the explanation is shown.
 */
export function useSearchEdited(): boolean {
  const applied = useMageXStore((state) => state.nlAppliedKey);
  const filterParams = useMageXStore((state) => state.filterParams);
  const conditions = useStore(dashboardPathwayStore, (state) => state.conditions);
  return applied !== null && selectionKey(conditions, filterParams) !== applied;
}

export default useMageXStore;
